import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Modal from 'react-overlays/Modal'
import Spinner from './Spinner'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules'
import SwiperCore from 'swiper'
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar'
import 'swiper/css/bundle'
import 'swiper/css'
import { FaShare } from 'react-icons/fa'

import { Layer, Map, MapProvider, Marker, Source } from 'react-map-gl/maplibre'
import { toast } from 'react-toastify'
import maplibregl from 'maplibre-gl'
import { geojsonToWKT } from '@terraformer/wkt'
import { callAPI } from '../hooks/api'
import { FaCircleInfo } from 'react-icons/fa6'
import { useAuth } from '../hooks/getAuth'
import FloodData from './FloodData'
import SoilData from './SoilData'
import CropData from './CropData'
import { pdf } from '@react-pdf/renderer'
import { toPng, toJpeg } from 'html-to-image'
import SoilProperties from './SoilProperties'
import RanchSelectModal from './RanchSelectModal'
import Watershed from './Watershed'
import ExecutiveSummary from './ExecutiveSummary'
import DeletePopupModal from './DeletePopupModal'
import WeatherHistory from './WeatherHistory'
import FeedbackPopupModal from './FeedbackPopupModal'
import InfoPopup from './InfoPopup'
import { isMobile, isSafari } from 'react-device-detect'
import useAbility from '@/hooks/use-ability'
import { Button } from './ui/button'
import UpgradeLayout from './upgrade-layout'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'
import { ParcelSingleReportPDF } from '../pages/pdf/pdf-report-parcel'
import { useAuthStore } from '@/components/PrivateRoute'

import { showReportLimitToast } from './toast/report-limit-toast'
import { useRefSize } from '@/hooks/use-ref-size'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { siteConfig } from '@/config/site'
import { base64ToBlobUrl } from '@/pages/pdf/_data/helper'

import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { undefinedToZero } from '@/hooks/utils'
import { ETierKey } from '@/pages/subscribe/_data/subscribe-constant'
import { useQueryState } from 'nuqs'
import {
  TrialLimitType,
  TRIAL_LIMIT_QUERY_KEY,
} from './trial-modal/trial-limit-modal'

export default function ParcelDetail({
  showModal,
  handleClose,
  property = {},
  logout,
  localAuth,
  selectedRanch,
  setSelectedRanch,
  drawParcelsByRanchId,
  onParcelDelete,
  ranches,
  setRanches,
  onRanchParcelDelete,
}) {
  const isDemoUser =
    localAuth.sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'

  const [_, setTrialLimitState] = useQueryState(TRIAL_LIMIT_QUERY_KEY)

  const [openSections, setOpenSections] = useState({
    executiveSummary: false,
    cropData: false,
    hydrologic: true,
    soilType: false,
    soilProperties: false,
    weatherData: false,
  })

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }
  const {
    checkAbility,
    getAbilityReport,
    getAbilityLimit,
    getAbilityOptionalLimit,
    invalidateQuery,
  } = useAbility()

  const enableOwnershipInformation =
    checkAbility('ownership_information') || isDemoUser
  const enableHydraScore = checkAbility('hydra_score')
  const enableSoilScore = checkAbility('soil_score')
  const enableReportGenerate = checkAbility('report_generation')
  const enableFloodMap = checkAbility('flood_maps')
  const enableSoilTypes = checkAbility('soil_types')
  const enableSoilProperties = checkAbility('soil_properties')
  const enableApnDisplay = checkAbility('apn_display')
  const enableAssemblyAndSenateDistricts = checkAbility(
    'assembly_and_senate_district'
  )
  const enableHistoricalClimateData = checkAbility('historical_climate_data')
  const enablePropertyLines = checkAbility('property_lines') || isDemoUser
  const enableCustomRanches = checkAbility('custom_ranches_portfolio')
  const numberOfReport = getAbilityLimit('number_of_reports')
  const numberOfReportRemain = getAbilityOptionalLimit(
    'number_of_remaining_reports'
  )
  const enableReportGeneration = checkAbility('report_generation')

  const isTrial = localAuth.tier === ETierKey.Trial

  const showReport = !enableReportGenerate && enableCustomRanches && !isTrial

  const [feedbackPopupModal, setFeedbackPopupModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [stateDistricts, setStateDistricts] = useState({
    'State Assembly District': '0',
    'State Senate District': '0',
  })
  const [floodData, setFloodData] = useState([])

  const [deletePopupModal, setDeletePopupModal] = useState(false)
  const [watershed, setWatershed] = useState({
    watershed: '',
    watershed_county: '',
    watershed_wd: [],
    watershed_gsa: [],
    watershed_hydrologic_region: '',
  })
  const [executiveSummaries, setExecutiveSummaries] = useState({})

  const getHoverText = (feature) => {
    const wdHovers = feature.properties.wd_info.flatMap((wd) => wd.wd_hover) // Extract wd_hover values

    const gsaHovers = feature.properties.gsa_info.flatMap(
      (gsa) => gsa.gsa_hover
    ) // Extract gsa_hover values

    return [...wdHovers, ...gsaHovers].join('\n\n') // Combine both with new lines
  }

  const transformExecutiveSummaries = (property) => {
    return {
      [property.feature.properties.hydrologic_info.subbasin_no]: {
        subbasin_no: property.feature.properties.hydrologic_info.subbasin_no,
        subbasin_name:
          property.feature.properties.hydrologic_info.subbasin_name,
        gsp_status: property.feature.properties.hydrologic_info.gsp_status,
        subbasin_status_update:
          property.feature.properties.hydrologic_info.subbasin_status_update,
        executive_summary:
          property.feature.properties.hydrologic_info.executive_summary,
        gsa_info: property.feature.properties.gsa_info,
        wd_info: property.feature.properties.wd_info,
      },
    }
  }

  useEffect(() => {
    if (Object.keys(property).length) {
      setExecutiveSummaries(transformExecutiveSummaries(property))
      setWatershed({
        watershed: property.feature.properties.hydrologic_info.watershed,
        watershed_county: property.feature.properties.county_name,
        watershed_wd: property.feature.properties.wd_info,
        watershed_gsa: property.feature.properties.gsa_info,
        watershed_hydrologic_region:
          property.feature.properties.hydrologic_info,
      })
    }
  }, [property])

  const [soilProperties, setSoilProperties] = useState([])
  const [showRanchSelectModal, setShowRanchSelectModal] = useState(false)

  const mapRef = useRef(null)
  const { auth } = useAuth()
  const navigate = useNavigate()

  const [selectedParcelIds, setSelectedParcelIds] = useState([])
  const [ranchName, setRanchName] = useState('')
  const [base64PDF, setBase64PDF] = useState(null)
  const [popupContent, setPopupContent] = useState(null)

  const authStore = useAuthStore((state) => state)

  const currentTier = authStore.tier.includes(' - ')
    ? authStore.tier.split(' - ')[0]
    : authStore.tier
  function onChange(e) {
    setRanchName(e.target.value)
  }

  const onRanchDropdownChange = (e) => {
    const ranch = ranches.find((item) => item.ranch_name === e.value)
    setSelectedRanch(ranch)
  }
  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }
  function addParcelToRanch(ranch) {
    // on every click push a new coordinate to the boxes array
    const {
      properties: {
        parcel_id,
        owner,
        address,
        mail_address,
        county_name,
        mkt_val_land,
        mkt_val_bldg,
        hs,
        ss,
        latitude,
        longitude,
        gsa_info,
        wd_info,
        acreage_calc,
        crop_composition,
        sv,
        soil_composition,
        fld_zone,
        main_crop,
        hydrologic_info,
        acreage_deeded,
      },
      ...remainingFeature
    } = property.feature

    const transformedFeature = {
      ...remainingFeature,
      properties: {
        parcel_id,
        owner,
        address,
        mail_address,
        county_name,
        mkt_val_land,
        mkt_val_bldg,
        hs,
        ss,
        latitude,
        longitude,
        gsa_info,
        wd_info,
        acreage_calc,
        crop_composition,
        sv,
        soil_composition,
        fld_zone,
        main_crop,
        hydrologic_info,
        acreage_deeded,
      },
    }
    transformedFeature.properties['mkt_val_land'] = undefinedToZero(
      transformedFeature.properties['mkt_val_land']
    )
    transformedFeature.properties['mkt_val_bldg'] = undefinedToZero(
      transformedFeature.properties['mkt_val_bldg']
    )

    if ('sv' in transformedFeature.properties) {
      const base64Image = btoa(transformedFeature.properties['sv'])
      transformedFeature.properties['sv'] =
        `data:image/png;base64,${base64Image}`
    }
    transformedFeature.properties['soil_properties'] = soilProperties

    // if (soilProperties.length == 0) {
    //   setLoading(true)
    //   var endpoint = '/get_soils_props_pt'
    //   var params = {
    //     lat: property.data.geolocation.lat,
    //     lon: property.data.geolocation.lng,
    //     session_key: localAuth.sessionKey,
    //   }

    //   callAPI(endpoint, params)
    //     .then((res) => {
    //       setLoading(false)
    //       if (res.status === 'success') {
    //         const splitSoilTypes = res.soil.map((soilType) => {
    //           const [name, valueWithUnit] = soilType.split(/:(.+)/)

    //           // Trim to remove leading/trailing whitespaces
    //           const nameTrimmed = name.trim()
    //           const valueWithUnitTrimmed = valueWithUnit.trim()

    //           // Extract unit from the first part of the string
    //           const match = nameTrimmed.match(/\[([^\]]+)\]/)
    //           const unit = match ? match[1] : ''

    //           // Combine the parts with the unit
    //           const firstPart = nameTrimmed.replace(` [${unit}]`, '')
    //           const secondPart = valueWithUnitTrimmed + (unit ? ' ' + unit : '')

    //           return [firstPart, secondPart]
    //         })
    //         setSoilProperties(splitSoilTypes)
    //         transformedFeature.properties['soil_properties'] = splitSoilTypes

    //         var endpoint = '/add_parcel_to_ranch'
    //         var params = {
    //           geojson_str: JSON.stringify(transformedFeature),
    //           ranch_id: ranch.ranch_id,
    //           session_key: localAuth.sessionKey,
    //         }
    //         setShowRanchSelectModal(false)
    //         setLoading(true)
    //         callAPI(endpoint, params)
    //           .then((res) => {
    //             setLoading(false)
    //             if (onParcelDelete) {
    //               onParcelDelete(property.id)
    //             }
    //             handleClose()
    //             setSelectedRanch(ranch)
    //             drawParcelsByRanchId(ranch.ranch_id)
    //           })
    //           .catch((e) => {
    //             setLoading(false)
    //           })
    //       }
    //     })
    // } else {
    //   var endpoint = '/add_parcel_to_ranch'
    //   var params = {
    //     geojson_str: JSON.stringify(transformedFeature),
    //     ranch_id: ranch.ranch_id,
    //     session_key: localAuth.sessionKey,
    //   }
    //   setShowRanchSelectModal(false)
    //   setLoading(true)
    //   callAPI(endpoint, params)
    //     .then((res) => {
    //       setLoading(false)
    //       if (onParcelDelete) {
    //         onParcelDelete(property.id)
    //       }
    //       handleClose()
    //       setSelectedRanch(ranch)
    //       drawParcelsByRanchId(ranch.ranch_id)
    //     })
    //     .catch((e) => {
    //       setLoading(false)
    //     })
    // }

    var endpoint = '/add_parcel_to_ranch'
    var params = {
      geojson_str: JSON.stringify(transformedFeature),
      ranch_id: ranch.ranch_id,
      session_key: localAuth.sessionKey,
    }
    setShowRanchSelectModal(false)
    setLoading(true)
    callAPI(endpoint, params)
      .then((res) => {
        if (onParcelDelete) {
          onParcelDelete(property.id)
        }
        handleClose()
        setSelectedRanch(ranch)
        drawParcelsByRanchId(ranch.ranch_id)

        endpoint = '/get_portfolio_summary'
        params = {
          ranch_id: ranch.ranch_id.toString(),
          return_update: true,
          session_key: localAuth.sessionKey,
        }
        callAPI(endpoint, params)
          .then((r) => {
            setLoading(false)
          })
          .catch((e) => {
            setLoading(false)
          })
      })
      .catch((e) => {
        setLoading(false)
      })

    // Example additional API call after completing all ranch operations
    endpoint = '/get_portfolio_summary'
    params = {
      ranch_id: ranch.ranch_id.toString(), // Use ranch_id directly
      return_update: true,
      session_key: localAuth.sessionKey,
    }
    callAPI(endpoint, params)
      .then((res) => {
        // Handle portfolio summary response
      })
      .catch((error) => {
        console.error(error)
      })
  }

  function createRanches(add = false) {
    setLoading(true)
    var endpoint = '/create_ranch'
    var params = {
      ranch_name: ranchName,
      session_key: localAuth.sessionKey,
    }
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        return
      }
      invalidateQuery()
      setRanches(res.ranches.slice().sort((a, b) => b.ranch_id - a.ranch_id))
      const ranch = res.ranches.find(
        (item) => item.ranch_name === res.ranch_name
      )
      setRanchName('')
      if (add) addParcelToRanch(ranch)
      else setSelectedRanch(ranch)
    })
  }

  async function onSubmit(e, add = false) {
    e.preventDefault()
    if (ranchName === '') {
      toast.warn('Must type ranch name first.')
      return
    } else {
      try {
        createRanches(add)
      } catch (error) {
        toast.warn(error.message)
      }
    }
  }

  const layerStyle = {
    id: 'Google Satellite Hybrid',
    type: 'raster',
    source: 'google_earth_hybrid',
    layout: { visibility: 'visible' },
  }

  function hydraScoreColor(hydraScore) {
    if (hydraScore >= 93) return '#008000'
    else if (hydraScore >= 79) return '#8FB935'
    else if (hydraScore >= 68) return '#E6E22E'
    else if (hydraScore >= 48) return '#E09C3B'
    else if (hydraScore === 'NR') return '#808080'
    else return '#E64747'
  }
  function hydraScoreRating(hydraScore) {
    if (hydraScore >= 93) return 'Excellent'
    else if (hydraScore >= 79) return 'Good'
    else if (hydraScore >= 68) return 'Average'
    else if (hydraScore >= 48) return 'Marginal'
    else if (hydraScore === 'NR') return 'Not Yet Rated'
    else return 'Poor'
  }
  useLayoutEffect(() => {
    async function getStateDistricts() {
      var lat = property.data.geolocation.lat
      var lon = property.data.geolocation.lng

      var endpoint = '/get_state_districts'
      var params = { lat: lat, lon: lon, session_key: localAuth.sessionKey }

      var res = await callAPI(endpoint, params)
      if (res.status === 'success') {
        setStateDistricts(res.state_districts)
      } else if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        setLoading(false)
        logout()
        return
      }
    }
    getStateDistricts()
  }, [])

  const renderBackdrop = (props) => <div className="backdrop" {...props} />

  SwiperCore.use([Autoplay, Navigation, Pagination])

  const addToPortfolioButtonClick = () => {
    setShowRanchSelectModal(true)
    setSelectedParcelIds([property.id])
  }

  const getImage = async (reportDiv) => {
    if (document.getElementById(reportDiv) === null) {
      return ''
    }

    if (isSafari || isMobile) {
      // console.log('Generating image for Safari or mobile')
      const element = document.getElementById(reportDiv)

      let dataUrl = ''
      const minDataLength = 2000000
      let i = 0
      const maxAttempts = 10

      while (dataUrl.length < minDataLength && i < maxAttempts) {
        try {
          dataUrl = await toPng(element, { quality: 0.1, cacheBust: true })
        } catch (error) {
          console.error('Error generating image:', error)
        }
        i += 1
      }

      return base64ToBlobUrl4GetImage(dataUrl)
    }

    return toJpeg(document.getElementById(reportDiv)).then(function (dataUrl) {
      const base64ImageData = dataUrl
      return base64ToBlobUrl4GetImage(base64ImageData)
    })
  }

  const base64ToBlobUrl4GetImage = (base64ImageData) => {
    if (base64ImageData === undefined || base64ImageData === '') {
      return ''
    }
    const contentType = isSafari || isMobile ? 'image/png' : 'image/jpeg'

    // console.log('contentType:', contentType)

    const byteCharacters = atob(
      base64ImageData.substr(`data:${contentType};base64,`.length)
    )
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)

      byteArrays.push(byteArray)
    }
    const blob = new Blob(byteArrays, { type: contentType })
    const blobUrl = URL.createObjectURL(blob)
    return blobUrl
  }

  const getWeatherPlots = async () => {
    // Create a new Date object for today
    const today = new Date()

    // Combine the year, month, and day components to form the yyyymmdd string
    const dts = `${today.getFullYear() - 41}0101`
    const dte = `${today.getFullYear() - 1}1231`

    var endpoint = '/get_weather_plots_from_pt'
    var params = {
      dts: dts,
      dte: dte,
      lat: property.data.geolocation.lat,
      lon: property.data.geolocation.lng,
      session_key: localAuth.sessionKey,
    }
    return callAPI(endpoint, params).then((res) => {
      return res
    })
  }

  function downloadPDF(base64String, projectName) {
    const downloadLink = document.createElement('a')
    const fileName = `Report - ${projectName}.pdf`
    downloadLink.href = base64String
    downloadLink.download = fileName
    downloadLink.click()
  }

  async function fetchFloodData(wkt) {
    try {
      var endpoint = '/get_flood_from_wkt'
      var params = {
        wkt: wkt,
        session_key: localAuth.sessionKey,
      }
      const res = await callAPI(endpoint, params)

      if (res.status === 'success') {
        return res.flood
      }
    } catch (error) {
      console.log(error)
    }
  }

  const generateLetterPdf = async (data) => {
    const ability = getAbilityReport()
    const blob = await pdf(
      <ParcelSingleReportPDF data={data} ability={ability} />
    ).toBlob()

    let base64String

    let reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      base64String = reader.result
      setBase64PDF(base64String)
    }
  }

  const runSingleReport = async () => {
    // Never put setLoading here. It will cover the property detail page and getElementById will return null.
    const {
      feature: {
        properties: {
          parcel_id,
          owner,
          address,
          mail_address,
          county_name,
          mkt_val_land,
          mkt_val_bldg,
          hs,
          ss,
          latitude,
          longitude,
          gsa_info,
          wd_info,
          acreage_calc,
          crop_composition,
          sv,
          soil_composition,
          fld_zone,
          main_crop,
          hydrologic_info,
          acreage_deeded,
        },
        ...remainingFeature2
      },
      ...remainingFeature
    } = property

    const transformedFeature = {
      ...remainingFeature2,
      properties: {
        parcel_id,
        owner,
        address,
        mail_address,
        county_name,
        mkt_val_land,
        mkt_val_bldg,
        hs,
        ss,
        latitude,
        longitude,
        gsa_info,
        wd_info,
        acreage_calc,
        crop_composition,
        sv,
        soil_composition,
        fld_zone,
        main_crop,
        hydrologic_info,
        acreage_deeded,
      },
    }

    const aerialImage = await getImage('aerial-view')
    const floodRiskImage = await getImage('floodrisk-view')
    setLoading(true)

    const floodRisk =
      !floodData || floodData.length === 0
        ? await fetchFloodData(geojsonToWKT(property.feature.geometry))
        : floodData

    const executiveSummary = transformExecutiveSummaries(property)
    const res = await getWeatherPlots()
    const transformedProperty = {
      ...remainingFeature,
      feature: transformedFeature,
    }

    try {
      const parcelData = {
        type: 'parcel',
        'aerial-view': aerialImage,
        property: transformedProperty,
        soilData: property.feature.properties.soil_composition,
        executive_summary: executiveSummary,
        generatedBy: auth.name,
        ppt: base64ToBlobUrl(res.plots.ppt),
        gdd: base64ToBlobUrl(res.plots.gdd),
        tmax: base64ToBlobUrl(res.plots.tmax),
        tmin: base64ToBlobUrl(res.plots.tmin),
        cdd: base64ToBlobUrl(res.plots.cdd),
        hdd: base64ToBlobUrl(res.plots.hdd),
        isMobile: isMobile,
        // add more data here
        soildTypes: property.feature.properties.soil_composition,
        soilProperties: property.feature.properties.soil_properties,
        floodRisk: floodRisk,
        floodRiskImage: floodRiskImage,
        stateDistricts: stateDistricts,
      }
      await generateLetterPdf(parcelData)
      setLoading(false)
    } catch (error) {
      console.error('Error in runSingleReport:', error)
      sendMessageToReactNative({ status: 'fail', error: error })
      setLoading(false)
    } finally {
    }
  }

  const getZoomLevel = () => {
    if (parseInt(property.data.acre) > 10000) {
      return 10
    } else if (parseInt(property.data.acre) > 5000) {
      return 11
    } else if (parseInt(property.data.acre) > 1000) {
      return 12
    } else if (parseInt(property.data.acre) > 200) {
      return 13
    } else {
      return 14
    }
  }

  const onClickUpgrade = () => {
    navigate(siteConfig.upgradeUrl)
  }

  const hydraScoreRef = useRef(null)
  const hydraScoreSize = useRefSize(hydraScoreRef)

  const [searchParams, setSearchParams] = useSearchParams()

  const ranchId = searchParams.get('ranchId')
  const parcelId = searchParams.get('parcelId')

  const isGenerateReport = ranchId && parcelId

  const pdfLinkRef = useRef(null)
  const runReportRef = useRef(null)
  const [waitingSpinner, setWaitingSpinner] = useState(0)

  useEffect(() => {
    if (document.getElementById('spinner') === null && isGenerateReport) {
      runSingleReport()
    } else {
      setTimeout(() => {
        setWaitingSpinner(waitingSpinner + 1)
      }, 2000)
    }
  }, [isGenerateReport, waitingSpinner])

  const sendMessageToReactNative = (status = {}, filePDF = {}) => {
    if (isGenerateReport) {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams)
        newParams.set('status', JSON.stringify(status))
        return newParams
      })
    }

    const message = {
      type: 'FROM_WEBVIEW',
      status: status,
      filePDF: filePDF,
    }

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message))
    }
  }

  useEffect(() => {
    if (base64PDF) {
      const filename = property.data.name
        ? property.data.name
        : property.feature.properties.county_name +
          ' - ' +
          property.feature.properties.parcel_id
      sendMessageToReactNative(
        { status: 'success' },
        { base64: base64PDF, filename: `Report - ${filename}` }
      )
      // downloadPDF(base64PDF, filename) // debug only
    }
  }, [base64PDF])

  if (loading) {
    return <Spinner />
  } else {
    return (
      <>
        {feedbackPopupModal && (
          <FeedbackPopupModal
            setFeedbackPopupModal={setFeedbackPopupModal}
            feedbackPopupModal={feedbackPopupModal}
            property={property}
            setLoading={setLoading}
          />
        )}

        {deletePopupModal && (
          <DeletePopupModal
            setDeletePopupModal={setDeletePopupModal}
            deletePopupModal={deletePopupModal}
            onDelete={onRanchParcelDelete}
            type={'Parcel'}
          />
        )}
        {popupContent && (
          <InfoPopup
            content={popupContent}
            onClose={() => setPopupContent(null)}
          />
        )}
        <div className="modal-example">
          <Modal
            className="modal"
            show={showModal}
            onHide={handleClose}
            renderBackdrop={renderBackdrop}
            style={{
              // display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
            }}
          >
            <div id="single-parcel-all" className="max-w-6xl px-3 mt-6 mx-auto">
              {showRanchSelectModal && (
                <RanchSelectModal
                  selectedRanch={selectedRanch}
                  onChange={onChange}
                  ranchName={ranchName}
                  onSubmit={(e) => onSubmit(e, true)}
                  setSelectedParcelIds={setSelectedParcelIds}
                  showRanchSelectModal={showRanchSelectModal}
                  setShowRanchSelectModal={setShowRanchSelectModal}
                  onRanchDropdownChange={onRanchDropdownChange}
                  ranches={ranches}
                  setRanches={setRanches}
                  addParcelToRanch={addParcelToRanch}
                />
              )}
              <div
                id="single-parcel-header"
                className="modal-header sticky top-0 z-[2001] bg-white flex items-center justify-between h-[68px]"
              >
                <div className="text-2xl w-full font-bold text-blue-900 flex gap-2 items-center h-full">
                  <p className="w-[225px]">Property Detail</p>
                  <div className="relative w-full">
                    <p
                      className={`${
                        isDemoUser || !enableOwnershipInformation
                          ? 'text-blue-50'
                          : ''
                      } w-full`}
                    >
                      {enableOwnershipInformation
                        ? property.data.name
                          ? `(${property.data.name})`
                          : ''
                        : ''}
                    </p>
                    {/* Overlay message */}
                    {!enableOwnershipInformation && (
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-start text-start z-10 gap-2 px-2 pr-4">
                        <p className=" text-black text- text-sm font-normal w-full line-clamp-3">
                          Upgrade to view Property Ownership information
                          including Owner Name, Owner Mailing Address, and APN
                        </p>
                        <Button
                          onClick={onClickUpgrade}
                          className="min-w-[151px]"
                          size="md"
                        >
                          Upgrade
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  {enableOwnershipInformation && (
                    <button
                      aria-label="Share"
                      onClick={(e) => {
                        e.stopPropagation() // Prevent triggering the card's onClick
                        navigator
                          .share({
                            title: 'Hydra score parcel(s)',
                            text: 'Click link to see shared parcel(s)',
                            url: `https://app.hydrascore.com/?locations=${btoa(
                              JSON.stringify([
                                {
                                  lat: property.data.geolocation.lat,
                                  lon: property.data.geolocation.lng,
                                },
                              ])
                            )}`,
                          })
                          .catch((error) => {
                            console.error('Sharing failed', error)
                          })
                      }}
                      title="Share this parcel"
                      className="mr-4" // Add some margin if necessary
                    >
                      <FaShare />
                    </button>
                  )}
                  <span className="close-button" onClick={handleClose}>
                    x
                  </span>
                </div>
              </div>

              <div
                id="single-parcel-images"
                className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <Swiper
                  slidesPerView={1}
                  navigation
                  pagination={{ type: 'progressbar' }}
                  effect="fade"
                  modules={[EffectFade]}
                  autoplay={{ delay: 3000 }}
                  style={{ zIndex: 2000 }}
                >
                  {property.data.imgUrls.map((url, index) => (
                    <SwiperSlide key={index}>
                      <div
                        className="relative w-full overflow-hidden h-[300px]"
                        style={{
                          background: `url(${url}) center no-repeat`,
                          backgroundSize: 'cover',
                        }}
                      ></div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <div className="m-[10px] rounded-[6px] relative">
                {/* Score */}
                <div
                  id="single-parcel-dashboard"
                  className="flex relative min-h-[120px] mt-3 shadow-md hover:shadow-lg rounded-md overflow-hidden transition-shadow duration-150 columns-6 justify-between items-center p-[10px]"
                >
                  {!enableHydraScore && !enableSoilScore && (
                    <div
                      className="h-full absolute top-0 left-0"
                      style={{
                        width: hydraScoreSize.width * 2.1,
                      }}
                    >
                      <UpgradeLayout
                        title="Upgrade to view Hydra Score & Soil Score."
                        className=""
                        needUpgrade={!enableHydraScore && !enableSoilScore}
                      />
                    </div>
                  )}

                  <div
                    ref={hydraScoreRef}
                    className="text-center w-full content-center"
                  >
                    <div className="flex-col">
                      <h2 className="underline relative ml-[20px]">
                        Hydra Score
                        {/* Info button placed next to the "Hydra Score" text */}
                        {enableHydraScore && (
                          <button
                            style={{
                              height: '20px',
                              width: '20px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              marginLeft: '2px', // Adjust this margin to control the spacing between the text and the button
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              border: '1px solid #00ac9c',
                              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                              transition:
                                'box-shadow 0.3s ease, transform 0.3s ease',
                            }}
                            onClick={(e) => {
                              e.stopPropagation() // Prevent event bubbling
                              setPopupContent(getHoverText(property.feature))
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.boxShadow =
                                '0 4px 10px rgba(0, 0, 0, 0.3)')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.boxShadow =
                                '0 2px 5px rgba(0, 0, 0, 0.2)')
                            }
                          >
                            <FaCircleInfo color="#00ac9c" />
                          </button>
                        )}
                      </h2>
                      <div className="w-full flex flex-row justify-center items-center">
                        <div
                          className="relative w-[70px]"
                          title={getHoverText(property.feature)}
                        >
                          <UpgradeLockIconLayout
                            className="h-full rounded-full items-center justify-center opacity-80"
                            iconSize={{ width: 24, height: 24 }}
                            show={!enableHydraScore && enableSoilScore}
                          />

                          {property.data.hydraScore && (
                            <>
                              <div className="relative">
                                {' '}
                                {/* Parent container to position the info icon */}
                                <CircularProgressbarWithChildren
                                  value={property.data.hydraScore}
                                  background
                                  backgroundPadding={6}
                                  styles={buildStyles({
                                    backgroundColor: hydraScoreColor(
                                      property.data.hydraScore
                                    ),
                                    pathColor: '#fff',
                                    trailColor: 'transparent',
                                  })}
                                >
                                  <div
                                    className="text-white"
                                    style={{ fontSize: 9 }}
                                  >
                                    Hydra
                                  </div>
                                  <strong
                                    className="text-white text-xs"
                                    style={{ fontSize: 25 }}
                                  >
                                    <span
                                      className={`${isDemoUser ? 'blur' : ''}`}
                                    >
                                      {property.data.hydraScore}
                                    </span>
                                  </strong>
                                  <div
                                    className="text-white"
                                    style={{ fontSize: 9 }}
                                  >
                                    Score
                                  </div>
                                </CircularProgressbarWithChildren>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center w-full content-center">
                    <div className="flex-col">
                      <h2 className="underline">Soil Score</h2>
                      <div className="w-full flex flex-row justify-center items-center">
                        <div className="w-[70px] relative">
                          <UpgradeLockIconLayout
                            className="h-full rounded-full items-center justify-center opacity-80"
                            iconSize={{ width: 24, height: 24 }}
                            show={!enableSoilScore && enableHydraScore}
                          />

                          {property.data.ss && (
                            <CircularProgressbarWithChildren
                              value={property.data.ss}
                              background
                              backgroundPadding={6}
                              styles={buildStyles({
                                backgroundColor: hydraScoreColor(
                                  property.data.ss
                                ),
                                pathColor: '#fff',
                                trailColor: 'transparent',
                              })}
                            >
                              <div
                                className="text-white"
                                style={{ fontSize: 9 }}
                              >
                                Soil
                              </div>
                              <strong
                                className="text-white text-xs"
                                style={{ fontSize: 25 }}
                              >
                                <span className={`${isDemoUser ? 'blur' : ''}`}>
                                  {property.data.ss}
                                </span>
                              </strong>
                              <div
                                className="text-white"
                                style={{ fontSize: 9 }}
                              >
                                Score
                              </div>
                            </CircularProgressbarWithChildren>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center w-full">
                    <h2 className="underline">Rating</h2>
                    <p className="font-semibold">
                      <span className={`${isDemoUser ? 'blur' : ''}`}>
                        {hydraScoreRating(property.data.hydraScore)}
                      </span>
                    </p>
                  </div>
                  <div className="text-center w-full">
                    <h2 className="underline">Acres</h2>
                    <p className="font-semibold">
                      <span className={`${isDemoUser ? 'blur' : ''}`}>
                        {property.feature.properties.hasOwnProperty(
                          'acreage_calc'
                        )
                          ? parseFloat(
                              property.feature.properties.acreage_calc
                            ).toFixed(2)
                          : property.feature.properties['Total Lot Size']}
                      </span>
                    </p>
                  </div>
                  <div className="text-center w-full">
                    <h2 className="underline">County</h2>
                    <div className="relative">
                      <UpgradeLockIconLayout
                        className="absolute min-w-[60px] justify-center"
                        show={!enableApnDisplay}
                      />
                      <p className="font-semibold">
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {capitalizeFirstLetter(
                            property.feature.properties.county_name
                          )}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-center w-full">
                    <h2 className="underline">Property APN</h2>
                    <div className="relative">
                      <UpgradeLockIconLayout
                        className="absolute min-w-[60px] justify-center"
                        show={!enableApnDisplay}
                      />
                      <p className="font-semibold">
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {property.data.apn}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-center w-full">
                    <h2 className="underline">Mailing Address</h2>
                    <div className="relative">
                      <UpgradeLockIconLayout
                        className="absolute min-w-[60px] justify-center"
                        show={!enableOwnershipInformation}
                      />
                      <p className="font-semibold">
                        <span
                          className={`${isDemoUser ? 'blur' : ''}
                          ${!enableOwnershipInformation ? 'line-clamp-1' : ''}
                          `}
                        >
                          {enableOwnershipInformation
                            ? property.data.address
                            : 'lorem'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                id="single-parcel-crop-data"
                className="min-h-[70px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div>
                  <h2
                    className="ml-[20px] mt-[20px] flex items-center cursor-pointer"
                    onClick={() => toggleSection('cropData')}
                  >
                    <p className="underline">Crop Data </p> &nbsp;&nbsp;{' '}
                    <p className="mt-[3px]">
                      {openSections.cropData ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )}
                    </p>
                  </h2>
                  {openSections.cropData && (
                    <CropData
                      cropData={property.feature.properties.crop_composition}
                      isDemoUser={isDemoUser}
                    />
                  )}
                </div>
              </div>
              <div className="m-[10px] rounded-[6px] relative">
                {/* ExecutiveSummary */}
                <div
                  id="single-parcel-summary"
                  className="min-h-[70px] relative justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150"
                >
                  <div>
                    <h2
                      className="ml-[20px] mt-[20px] flex items-center cursor-pointer"
                      onClick={() => toggleSection('executiveSummary')}
                    >
                      <p className="underline">Executive Summary </p>{' '}
                      &nbsp;&nbsp;{' '}
                      <p className="mt-[3px]">
                        {openSections.executiveSummary ? (
                          <FaChevronDown />
                        ) : (
                          <FaChevronRight />
                        )}
                      </p>
                    </h2>
                    {openSections.executiveSummary && (
                      <ExecutiveSummary
                        executiveSummaries={executiveSummaries}
                        isDemoUser={isDemoUser}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div
                id="single-parcel-admin"
                className="min-h-[70px] shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div>
                  <h2
                    className="ml-[20px] mt-[20px] flex items-center cursor-pointer"
                    onClick={() => toggleSection('hydrologic')}
                  >
                    <p className="underline">Hydrologic </p> &nbsp;&nbsp;{' '}
                    <p className="mt-[3px]">
                      {openSections.hydrologic ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )}
                    </p>
                  </h2>
                  {openSections.hydrologic && (
                    <Watershed watershed={watershed} isDemoUser={isDemoUser} />
                  )}
                </div>
              </div>
              <div id="single-parcel-buttons" className="relative">
                <div
                  className="flex min-h-[120px] shadow-md hover:shadow-lg
                rounded-md overflow-hidden transition-shadow duration-150 columns-5 justify-between items-center m-[10px] relative"
                >
                  <UpgradeLayout
                    title="Upgrade to create a ranch and run reports."
                    needUpgrade={!enableCustomRanches && !enableReportGenerate}
                  />
                  <div className="text-center w-full content-center">
                    <div className="relative">
                      <UpgradeLayout
                        title="Upgrade to create a ranch."
                        needUpgrade={
                          !enableCustomRanches && enableReportGenerate
                        }
                      />
                      {property.type === 'ranch' ? (
                        <button
                          // disabled={document.getElementById('spinner') !== null}
                          onClick={() => {
                            if (document.getElementById('spinner') !== null) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }
                            setDeletePopupModal(true)
                          }}
                          className={` px-7 py-3 bg-red-600 text-white font-medium text-sm uppercase rounded shadow-md hover:bg-red-700 hover:shadow-lg focus:bg-red-700 focus:shadow-lg w-[50%] text-center transition duration-150 ease-in-out btn btn-custom delete-parcel`}
                        >
                          Delete From Ranch
                        </button>
                      ) : (
                        <button
                          disabled={!enableCustomRanches}
                          onClick={() => {
                            if (document.getElementById('spinner') !== null) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }
                            addToPortfolioButtonClick()
                          }}
                          className={` px-7 py-3 bg-blue-600 text-white font-medium text-sm uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg w-[50%] text-center transition duration-150 ease-in-out btn btn-custom add-to-ranch`}
                        >
                          Add to Ranch
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-center w-full content-center min-h-[120px] relative">
                    <UpgradeLayout
                      position="min-h-[120px] px-1 py-0 top-0 left-0"
                      title="Upgrade to generate detailed PDF reports of an individual
              property, ranch, or collateral group."
                      className="min-h-[120px]"
                      needUpgrade={showReport}
                    />
                    <div>
                      {base64PDF ? (
                        <button
                          ref={pdfLinkRef}
                          onClick={() => {
                            downloadPDF(
                              base64PDF,
                              property.data.name
                                ? property.data.name
                                : property.feature.properties.county_name +
                                    ' - ' +
                                    property.feature.properties.parcel_id
                            )
                          }}
                          className={` px-7 py-3 bg-primary text-white font-medium text-sm uppercase rounded shadow-md hover:bg-[#01589d] hover:shadow-lg focus:bg-[#01589d] focus:shadow-lg w-[50%] text-center transition duration-150 ease-in-out btn btn-custom`}
                        >
                          {' '}
                          Download Report
                        </button>
                      ) : (
                        <button
                          id={'runSingleReportButton'}
                          ref={runReportRef}
                          onClick={async () => {
                            if (isDemoUser) {
                              toast.warn(
                                'This action is not available for demo users'
                              )
                              return
                            }

                            if (document.getElementById('spinner') !== null) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }

                            if (!enableReportGeneration && currentTier === ETierKey.Trial) {
                              return setTrialLimitState(TrialLimitType.REPORTS_NOT_INCLUDED)
                            }

                            if (numberOfReportRemain === 0) {
                              showReportLimitToast({
                                tier: currentTier,
                                numberOfReport: numberOfReport,
                              })
                              return
                            }

                            if (runReportRef.current) {
                              runReportRef.current.innerText = 'Processing...'
                              runReportRef.current.disabled = true
                            }

                            try {
                              await runSingleReport()
                            } catch (error) {
                              toast.error(
                                'Failed to run the report. Please try again.'
                              )
                            } finally {
                              if (runReportRef.current) {
                                runReportRef.current.innerText =
                                  'Run Single Report'
                                runReportRef.current.disabled = false
                              }
                            }
                          }}
                          className={` px-7 py-3 bg-primary text-white font-medium text-sm uppercase rounded shadow-md hover:bg-[#01589d] hover:shadow-lg focus:bg-[#01589d] focus:shadow-lg w-[50%] text-center transition duration-150 ease-in-out btn btn-custom`}
                        >
                          Run Single Report
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="single-parcel-mapview"
                className="flex flex-col md:flex-row max-w-6xl p-4 min-h-[120px] shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 columns-5 justify-between items-center m-[10px] lg:space-x-5"
              >
                <div className=" w-full ">
                  <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                    {enableOwnershipInformation ? (
                      <li className="flex items-center whitespace-nowrap">
                        Owner:&nbsp;
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {property.feature.properties.owner}
                        </span>
                      </li>
                    ) : (
                      <li className="items-center flex flex-row gap-1">
                        <p>Owner:</p>
                        <div className="relative">
                          <UpgradeLockIconLayout
                            className="min-h-[20px]"
                            show={true}
                          />
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {property.feature.properties.owner || 'lorem'}
                          </span>
                        </div>
                      </li>
                    )}
                  </ul>
                  {
                    <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                      <li className="flex items-center whitespace-wrap">
                        Acres:&nbsp;
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {property.feature.properties.hasOwnProperty(
                            'acreage_calc'
                          )
                            ? parseFloat(
                                property.feature.properties.acreage_calc
                              ).toFixed(2)
                            : property.feature.properties['Total Lot Size']}
                        </span>
                      </li>
                    </ul>
                  }
                  {property.feature.properties.county ||
                    (property.feature.properties.county_name && (
                      <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                        <li className="flex items-center whitespace-wrap">
                          County:&nbsp;
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {property.feature.properties.county ||
                              property.feature.properties.county_name}
                          </span>
                        </li>
                      </ul>
                    ))}
                  <div
                    className={`relative ${
                      !enableAssemblyAndSenateDistricts ? 'mt-6' : ''
                    }`}
                  >
                    <UpgradeLayout
                      className="flex flex-row"
                      needUpgrade={!enableAssemblyAndSenateDistricts}
                      title="Upgrade to view Assembly and Senate Districts."
                    />
                    {Object.entries(stateDistricts).map(([key, value]) => (
                      <ul
                        key={key}
                        className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1"
                      >
                        <li className="flex items-center whitespace-wrap">
                          {key}:&nbsp;
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {value}
                          </span>
                        </li>
                      </ul>
                    ))}
                  </div>
                </div>
                <div className="w-full h-[200px] md:h-[400px] z-10 overflow-x-hidden mt-6 md:mt-0 md:ml-2">
                  <MapProvider>
                    <Map
                      id="aerial-view"
                      ref={mapRef}
                      initialViewState={{
                        longitude: property.data.geolocation.lng,
                        latitude: property.data.geolocation.lat,
                        zoom: getZoomLevel(),
                      }}
                      scrollZoom={false}
                      dragPan={false}
                      doubleClickZoom={false}
                      style={{ height: '100%', width: '100%' }}
                      preserveDrawingBuffer={true}
                    >
                      <Source
                        id="google_earth_hybrid"
                        type="raster"
                        tiles={[
                          'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                        ]}
                        tileSize={256}
                      >
                        <Layer {...layerStyle} />
                      </Source>
                      {enablePropertyLines && property.feature ? (
                        <Source
                          id="property-source"
                          type="geojson"
                          data={property.feature}
                        >
                          <Layer
                            {...{
                              source: 'property-source',
                              id: 'property-layer',
                              type: 'fill',
                              layout: { visibility: 'visible' },
                              paint: {
                                'fill-color': '#FFA500',
                                'fill-opacity': 0.2,
                                'fill-outline-color': '#FFA500',
                              },
                            }}
                          />
                          <Layer
                            {...{
                              source: 'property-source',
                              id: 'property-layer-outline',
                              type: 'line',
                              layout: {
                                visibility: 'visible',
                                'line-join': 'round',
                                'line-cap': 'round',
                              },
                              paint: {
                                'line-color': '#FFA500',
                                'line-width': 4,
                              },
                            }}
                          />
                        </Source>
                      ) : (
                        <Marker
                          longitude={property.data.geolocation.lng}
                          latitude={property.data.geolocation.lat}
                          popup={new maplibregl.Popup().setText(
                            property.data.address
                          )}
                        ></Marker>
                      )}
                    </Map>
                  </MapProvider>
                </div>
              </div>
              <div
                id="single-parcel-soil-type"
                className="min-h-[70px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div>
                  <h2
                    className="ml-[20px] mt-[20px] flex items-center cursor-pointer"
                    onClick={() => toggleSection('soilType')}
                  >
                    <p className="underline">Soil Types </p> &nbsp;&nbsp;{' '}
                    <p className="mt-[3px]">
                      {openSections.soilType ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )}
                    </p>
                  </h2>
                  {openSections.soilType && (
                    <div className="relative">
                      <UpgradeLayout
                        title="Upgrade to view Soil Types."
                        needUpgrade={!enableSoilTypes}
                      />
                      <SoilData
                        soilData={property.feature.properties.soil_composition}
                        localAuth={localAuth}
                        isDemoUser={isDemoUser}
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* <div
                id="single-parcel-soil-properties"
                className={cn(
                  'min-h-[70px] max-h-[400px] justify-between items-center shadow-md hover:shadow-lg rounded-md overflow-y-auto transition-shadow duration-150 m-[10px]',
                  {
                    'overflow-y-hidden': !enableSoilProperties,
                  }
                )}
              >
                <div>
                  <h2 className="ml-[20px] mt-[20px] flex items-center cursor-pointer" onClick={() => toggleSection('soilProperties')}>
                    <p className='underline'>Soil Properties </p> &nbsp;&nbsp; <p className='mt-[3px]'>{openSections.soilProperties ? <FaChevronDown /> : <FaChevronRight />}</p>
                  </h2>
                  {openSections.soilProperties && (
                    <div>
                      {!enableSoilProperties && (
                        <div className="mt-[20px] h-[330px]">
                          <UpgradeLayout
                            position="h-[300px]"
                            title="Upgrade to view Soil Properties."
                            needUpgrade={!enableSoilProperties}
                          />
                        </div>
                      )}
                      <SoilProperties
                        property={property}
                        localAuth={localAuth}
                        setSoilProperties={setSoilProperties}
                        soilProperties={soilProperties}
                        isDemoUser={isDemoUser}
                      />
                    </div>
                  )}
                </div>
              </div> */}
              {!isDemoUser && (
                <div
                  id="single-parcel-flood"
                  className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
                >
                  <div>
                    <h2 className="underline ml-[20px] mt-[20px]">
                      Flood Risk:
                    </h2>
                    <div className="relative">
                      <UpgradeLayout
                        title="Upgrade to view Flood Risk."
                        needUpgrade={!enableFloodMap}
                      />
                      <FloodData
                        setFloodData={setFloodData}
                        wkt={geojsonToWKT(property.feature.geometry)}
                        apn={property.data.apn}
                        lat={property.data.geolocation.lat}
                        lon={property.data.geolocation.lng}
                        localAuth={localAuth}
                      />
                      <div />
                    </div>
                  </div>
                </div>
              )}
              {!isDemoUser &&
                (enableHistoricalClimateData ? (
                  <div
                    id="single-parcel-weather-history"
                    className="min-h-[70px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
                  >
                    <div>
                      <h2
                        className="ml-[20px] mt-[20px] flex items-center cursor-pointer"
                        onClick={() => toggleSection('weatherData')}
                      >
                        <p className="underline">
                          Historical Weather Data (~10 years){' '}
                        </p>{' '}
                        &nbsp;&nbsp;{' '}
                        <p className="mt-[3px]">
                          {openSections.weatherData ? (
                            <FaChevronDown />
                          ) : (
                            <FaChevronRight />
                          )}
                        </p>
                      </h2>
                      {openSections.weatherData && (
                        <WeatherHistory
                          lat={property.data.geolocation.lat}
                          lon={property.data.geolocation.lng}
                          localAuth={localAuth}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    id="single-parcel-weather-history"
                    className="h-[180px]  justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px] relative"
                  >
                    <UpgradeLayout
                      title="Upgrade to view Historical Climate Data."
                      className="h-[180px]"
                      needUpgrade={!enableHistoricalClimateData}
                    />
                  </div>
                ))}
              <div
                id="single-feedback-buttons"
                className="fixed bottom-0 right-10 z-[100]"
              >
                <div className="flex overflow-hidden justify-between items-center w-[300px]">
                  <div className="text-center w-full content-center">
                    <div className="relative">
                      <button
                        onClick={() => {
                          setFeedbackPopupModal(true)
                        }}
                        className="px-7 py-3 bg-blue-600 text-white font-medium text-sm uppercase rounded-t-2xl shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg w-full text-center transition duration-150 ease-in-out btn"
                      >
                        Send Us Feedback
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="m-20"></div>
            </div>
          </Modal>
        </div>
      </>
    )
  }
}
